import { Injectable, NgModule } from '@angular/core';
import {
  BrowserModule,
  HAMMER_GESTURE_CONFIG,
  HammerGestureConfig,
  HammerModule,
} from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import {
  BrowserAnimationsModule,
  provideAnimations,
} from '@angular/platform-browser/animations';
import { NewLandingPageComponent } from './components/new-landing-page/new-landing-page.component';
import { MaterialModule } from './modules/material/material.module';
import { CommonlyUsedModule } from './modules/commonly-used/commonly-used-modules';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { HttpInterceptorService } from './services/http-interceptor.service';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { LoginComponent } from './components/login/login.component';
import { CommonModule } from '@angular/common';
import { MathjaxTestComponent } from './components/mathjax-test/mathjax-test.component';
import { MathjaxModule } from 'mathjax-angular';
import { UserProfileComponent } from './components/user-profile/user-profile.component';
import { NotifierModule, NotifierOptions } from 'angular-notifier';
import { UserDetailsComponent } from './components/user-details/user-details.component';
import { QuestionBankComponent } from './components/question-bank/question-bank.component';
import { CreateQuestionComponent } from './components/create-question/create-question.component';
import { ManageCourseMaterialContainerComponent } from './components/manage-course-material-container/manage-course-material-container.component';
import { AssignCourseMaterialComponent } from './components/assign-course-material/assign-course-material.component';
import { ManageCourseMaterialComponent } from './components/manage-course-material/manage-course-material.component';
import { StudentMaterialComponent } from './components/student-material/student-material.component';
import { StudentClassesComponent } from './components/student-classes/student-classes.component';
import { ViewResultsComponent } from './components/view-results/view-results.component';
import { ListOfTestsComponent } from './components/list-of-tests/list-of-tests.component';
import { QandaComponent } from './components/qanda/qanda.component';
import { TopicComponent } from './components/topic/topic.component';
import { AnswerKeyComponent } from './components/answer-key/answer-key.component';
import { NotesComponent } from './components/notes/notes.component';
import { PaymentComponent } from './components/payment/payment.component';
import { AssignTestComponent } from './components/assign-test/assign-test.component';
import { ScoreAnalysisComponent } from './components/score-analysis/score-analysis.component';
import { CompletedTestComponent } from './components/completed-test/completed-test.component';
import { ReportComponent } from './components/report/report.component';
import { ReportErrorLogComponent } from './components/report-error-log/report-error-log.component';
import { ReportAnalysisComponent } from './components/report-analysis/report-analysis.component';
import { StudentCreationComponent } from './components/student-creation/student-creation.component';
import { BatchComponent } from './components/batch/batch.component';
import { OnlineTestComponent } from './components/online-test/online-test.component';
import { NotificationComponent } from './components/notification/notification.component';
import { ClassesComponent } from './components/classes/classes.component';
import { InfoPopupComponent } from './components/info-popup/info-popup.component';
import { TermsComponent } from './components/terms/terms.component';
import { NoopAnimationsModule } from '@angular/platform-browser/animations';
import { UsersComponent } from './components/users/users.component';
import { ConfirmationService } from 'primeng/api';
import { CreateModuleComponent } from './components/create-module/create-module.component';
import { CreateMultiDataComponent } from './modules/commonly-used/create-multi-data/create-multi-data.component';
import { CommonSideNavbarComponent } from './modules/commonly-used/common-side-navbar/common-side-navbar.component';
import { NewDashboardComponent } from './components/new-dashboard/new-dashboard.component';
import { CommonUserInfoComponent } from './modules/commonly-used/common-user-info/common-user-info.component';
import { ClassesCalendarComponent } from './components/classes-calendar/classes-calendar.component';
import { TestCreationComponent } from './components/test-creation/test-creation.component';
import { QuestionDistributionPatternComponent } from './components/test-creation/dialogs/question-distribution-pattern/question-distribution-pattern.component';
import { ConfirmationDialogComponent } from './components/test-creation/dialogs/confirmation-dialog/confirmation-dialog.component';
import { ManualReplaceDialogComponent } from './components/test-creation/dialogs/manual-replace-dialog/manual-replace-dialog.component';
import { PracticeTestComponent } from './components/practice-test/practice-test.component';
import { AcademicCoachingComponent } from './components/academic-coaching/academic-coaching.component';
import { NumbersOnlyDirective } from './directives/numbers-only.directive';
import { CountryCodeDirective } from './directives/country-code.directive';
import { PaymentsManagerComponent } from './components/payment-manager/payments-manager.component';
import { StudentPaymentComponent } from './components/student-payment/student-payment.component';
import { TestScoringPatternComponent } from './components/test-creation/scoring/test-scoring-pattern/test-scoring-pattern.component';
import { PrintTestComponent } from './components/print-test/print-test.component';
import { SignInComponent } from './components/sign-in/sign-in.component';
import { ChartComponent } from './components/chart/chart.component';
import { ManageClassesComponent } from './components/manage-classes/manage-classes.component';
import { ClassDetailsComponent } from './components/class-details/class-details.component';
import { ConfirmDialogComponent } from './components/confirm-dialog/confirm-dialog.component';
import { ClassFormComponent } from './components/class-form/class-form.component';

@Injectable()
export class MyHammerConfig extends HammerGestureConfig {
  override overrides = <any>{
    swipe: { direction: (window as any).Hammer.DIRECTION_ALL },
  };
}
/**
 * Custom angular notifier options
 */
const customNotifierOptions: NotifierOptions = {
  position: {
    horizontal: {
      position: 'right',
      distance: 12,
    },
    vertical: {
      position: 'top',
      distance: 12,
      gap: 10,
    },
  },
  theme: 'material',
  behaviour: {
    autoHide: 5000,
    onClick: 'hide',
    onMouseover: 'pauseAutoHide',
    showDismissButton: true,
    stacking: 4,
  },
  animations: {
    enabled: true,
    show: {
      preset: 'slide',
      speed: 300,
      easing: 'ease',
    },
    hide: {
      preset: 'fade',
      speed: 300,
      easing: 'ease',
      offset: 50,
    },
    shift: {
      speed: 300,
      easing: 'ease',
    },
    overlap: 150,
  },
};

const mathjaxConfig = {
  config: {
    loader: {
      load: ['output/svg', '[tex]/require', '[tex]/ams'],
    },
    tex: {
      inlineMath: [
        ['$', '$'],
        ['\\(', '\\)'],
      ],
      packages: ['base', 'require', 'ams'],
    },
    svg: { fontCache: 'global' },
  },
  src: 'https://cdn.jsdelivr.net/npm/mathjax@3.2.2/es5/startup.js',
};

@NgModule({
  declarations: [
    AppComponent,
    NewLandingPageComponent,
    DashboardComponent,
    LoginComponent,
    MathjaxTestComponent,
    UserProfileComponent,
    UserDetailsComponent,
    QuestionBankComponent,
    CreateQuestionComponent,
    ManageCourseMaterialContainerComponent,
    AssignCourseMaterialComponent,
    ManageCourseMaterialComponent,
    StudentMaterialComponent,
    StudentClassesComponent,
    ViewResultsComponent,
    ListOfTestsComponent,
    QandaComponent,
    TopicComponent,
    AnswerKeyComponent,
    NotesComponent,
    PaymentComponent,
    AssignTestComponent,
    ScoreAnalysisComponent,
    CompletedTestComponent,
    ReportComponent,
    ReportErrorLogComponent,
    ReportAnalysisComponent,
    StudentCreationComponent,
    BatchComponent,
    OnlineTestComponent,
    NotificationComponent,
    ClassesComponent,
    InfoPopupComponent,
    TermsComponent,
    UsersComponent,
    CreateModuleComponent,
    CreateMultiDataComponent,
    CommonSideNavbarComponent,
    NewDashboardComponent,
    CommonUserInfoComponent,
    ClassesCalendarComponent,
    TestCreationComponent,
    QuestionDistributionPatternComponent,
    ConfirmationDialogComponent,
    ManualReplaceDialogComponent,
    PracticeTestComponent,
    AcademicCoachingComponent,
    NumbersOnlyDirective,
    CountryCodeDirective,
    PaymentsManagerComponent,
    StudentPaymentComponent,
    NumbersOnlyDirective,
    CountryCodeDirective,
    TestScoringPatternComponent,
    PrintTestComponent,
    SignInComponent,
    ChartComponent,
    ManageClassesComponent,
    ClassDetailsComponent,
    ConfirmDialogComponent,
    ClassFormComponent,
  ],
  imports: [
    BrowserModule,
    HammerModule,
    CommonModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    MaterialModule,
    CommonlyUsedModule,
    MathjaxModule.forRoot(mathjaxConfig),
    NotifierModule.withConfig(customNotifierOptions),
    NoopAnimationsModule,
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpInterceptorService,
      multi: true,
    },
    provideAnimations(),
    { provide: HAMMER_GESTURE_CONFIG, useClass: MyHammerConfig },
    ConfirmationService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
